import { sumBy } from 'lodash';
import { DateTime, Interval } from 'luxon';
import { match } from 'ts-pattern';
import { MAX_DATE, MIN_DATE } from './Constants';
import { DayOfWeek, DayOfWeekAvailability, hasSomeDayAvailability, isAvailableOnDay } from './DaysOfWeek';
import { MAX_BOOKING_TIME_IN_ADVANCE } from './OrderDateRangeUtilities';
import { urlDateToDateTime } from './UrlDate';

export interface FoodItemAvailaibilityFields extends Record<DayOfWeekAvailability, boolean> {
  startDate: string;
  endDate: string;
}

type ValidateFoodItemAvailabilityResult =
  | { valid: false; reason: 'availabilityDay'; date: DateTime } // fails due to available* not providing enough availability
  | { valid: false; reason: 'availabilityLimit'; date: DateTime } // fails due to limitation imposed by start/end dates
  | { valid: true; reason?: never; date?: never }; // no failure

const isMinDate = (date: DateTime) => date.toMillis() === MIN_DATE.toMillis();
const isMaxDate = (date: DateTime) => date.toMillis() === MAX_DATE.toMillis();

// it's possible to have 0, 1, or 2 periods of explicit unavailabilty for an item, see graphic here:
// https://www.notion.so/Rotating-Food-Items-0b4f3d05b61d4774a8f88a3f8d23bd82#cd6c8c2db2194740884d715a760d28ac
function getFoodItemUnavailabilityRanges(dates: FoodItemAvailaibilityFields, shefTimeZone: string): Interval[] {
  const startDate = urlDateToDateTime(dates.startDate, shefTimeZone);
  const endDate = urlDateToDateTime(dates.endDate, shefTimeZone);

  return match({ startIsMin: isMinDate(startDate), endIsMax: isMaxDate(endDate), startLtMax: startDate < endDate })
    .with({ startIsMin: true, endIsMax: true }, () => [])
    .with({ startIsMin: false, endIsMax: true }, () => [Interval.fromDateTimes(MIN_DATE, startDate)])
    .with({ startIsMin: true, endIsMax: false }, () => [Interval.fromDateTimes(endDate, MAX_DATE)])
    .with({ startIsMin: false, endIsMax: false, startLtMax: true }, () => [
      Interval.fromDateTimes(MIN_DATE, startDate),
      Interval.fromDateTimes(endDate, MAX_DATE),
    ])
    .with({ startIsMin: false, endIsMax: false, startLtMax: false }, () => [Interval.fromDateTimes(endDate, startDate)])
    .exhaustive();
}

// it's possible to have 1 or 2 periods of explicit availabilities for an item, see graphic here:
// https://www.notion.so/Rotating-Food-Items-0b4f3d05b61d4774a8f88a3f8d23bd82#cd6c8c2db2194740884d715a760d28ac
function getFoodItemAvailabilityRanges(dates: FoodItemAvailaibilityFields, shefTimeZone: string): Interval[] {
  const startDate = urlDateToDateTime(dates.startDate, shefTimeZone);
  const endDate = urlDateToDateTime(dates.endDate, shefTimeZone);

  return match({ startIsMin: isMinDate(startDate), endIsMax: isMaxDate(endDate), startLtMax: startDate < endDate })
    .with({ startIsMin: true, endIsMax: true }, () => [Interval.fromDateTimes(MIN_DATE, MAX_DATE)])
    .with({ startIsMin: false, endIsMax: true }, () => [Interval.fromDateTimes(startDate, MAX_DATE)])
    .with({ startIsMin: true, endIsMax: false }, () => [Interval.fromDateTimes(MIN_DATE, endDate)])
    .with({ startIsMin: false, endIsMax: false, startLtMax: true }, () => [Interval.fromDateTimes(startDate, endDate)])
    .with({ startIsMin: false, endIsMax: false, startLtMax: false }, () => [
      Interval.fromDateTimes(MIN_DATE, endDate),
      Interval.fromDateTimes(startDate, MAX_DATE),
    ])
    .exhaustive();
}

interface FoodItemModelWithUnavailabilities extends FoodItemAvailaibilityFields {
  unavailabilities: Interval[];
}

function addFoodItemAvailabilityRanges(
  foodItems: FoodItemAvailaibilityFields[],
  shefTimeZone: string
): FoodItemModelWithUnavailabilities[] {
  return foodItems.map((foodItem) => ({
    ...foodItem,
    unavailabilities: getFoodItemUnavailabilityRanges(foodItem, shefTimeZone),
  }));
}

interface ValidateFoodItemAvailabilityForDateRangesParams {
  dateRanges: Interval[];
  minimumAvailable: number;
  foodItems: FoodItemAvailaibilityFields[];
  shefTimeZone: string;
}

function getFoodItemsDayOfWeekAvailabilityCount(foodItems: FoodItemAvailaibilityFields[]) {
  return [
    Number(sumBy(foodItems, `availableMonday`)) || 0,
    Number(sumBy(foodItems, `availableTuesday`)) || 0,
    Number(sumBy(foodItems, `availableWednesday`)) || 0,
    Number(sumBy(foodItems, `availableThursday`)) || 0,
    Number(sumBy(foodItems, `availableFriday`)) || 0,
    Number(sumBy(foodItems, `availableSaturday`)) || 0,
    Number(sumBy(foodItems, `availableSunday`)) || 0,
  ];
}

// Given a set of food items and date ranges, it calculates if food items meet a minimum required supply.
// If it does not, it responds with the reason for why it failed to meet the supply requirements.
// This can be used to provide a shef's availability as a set of date ranges to calculate if there would
// be enough supply for those dates.
export function validateFoodItemAvailabilityForDateRanges({
  dateRanges,
  minimumAvailable,
  foodItems,
  shefTimeZone,
}: ValidateFoodItemAvailabilityForDateRangesParams): ValidateFoodItemAvailabilityResult {
  const dayOfWeekAvailability = getFoodItemsDayOfWeekAvailabilityCount(foodItems);
  const foodItemsWithUnavailabilites = addFoodItemAvailabilityRanges(foodItems, shefTimeZone);

  // validate range
  for (const dateRange of dateRanges) {
    for (
      let date = dateRange.start.setZone(shefTimeZone);
      date < dateRange.end;
      date = date.plus({ days: 1 }).setZone(shefTimeZone)
    ) {
      // eslint-disable-next-line no-restricted-properties
      const dayOfWeekCount = dayOfWeekAvailability[date.weekday - 1];
      if (dayOfWeekCount < minimumAvailable) return { valid: false, reason: 'availabilityDay', date };

      // calculate the count of food items that `available` + DoW would
      // make it available, but the limitation specified by the start/end
      // dates restrict it fromm being available
      const dateUnavailableCount = foodItemsWithUnavailabilites
        .map((foodItem) => {
          const isAvailableToday =
            // eslint-disable-next-line no-restricted-properties
            foodItem[`available${getDayOfWeek(date.weekday)}` as keyof FoodItemAvailaibilityFields];
          if (!isAvailableToday) return 0;
          return Number(foodItem.unavailabilities.some((int) => int.contains(date)));
        })
        .reduce((acc, curr) => acc + curr, 0);

      // calculate if new total falls below minimum allowed value
      if (dayOfWeekCount - dateUnavailableCount < minimumAvailable) {
        return { valid: false, reason: 'availabilityLimit', date };
      }
    }
  }

  // everything has passed, so the food item has valid availability
  return { valid: true };
}

interface ValidateFoodItemAvailabilityForDateParams {
  date: DateTime;
  minimumAvailable: number;
  foodItems: FoodItemAvailaibilityFields[];
  shefTimeZone: string;
}

// Given a set of food items and a single date, it calculates if food items meet a minimum required supply.
export const validateFoodItemAvailabilityForDate = ({
  date,
  minimumAvailable,
  foodItems,
  shefTimeZone,
}: ValidateFoodItemAvailabilityForDateParams): ValidateFoodItemAvailabilityResult =>
  validateFoodItemAvailabilityForDateRanges({
    dateRanges: [dateTimeToDayInterval(date)],
    minimumAvailable,
    foodItems,
    shefTimeZone,
  });

// takes a date time and converts it to an interval that represent a whole day
export const dateTimeToDayInterval = (date: DateTime): Interval => Interval.fromDateTimes(date, date.plus({ days: 1 }));

// takes a list of date times and converts them to intervals that represent whole days
// then merges day intervals into largest consecutive intervals possible
export const dateTimesToDateRanges = (dates: DateTime[]): Interval[] =>
  Interval.merge(dates.map(dateTimeToDayInterval));

const allDaysOfWeek: DayOfWeek[] = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
export function getDayOfWeek(index: number): DayOfWeek {
  if (index === 0 || index > allDaysOfWeek.length) throw new Error(`invalid weekday index: ${index}`);
  return allDaysOfWeek[index - 1];
}

export function isAvailableSometimeWithinBookingRange(
  foodItem: FoodItemAvailaibilityFields,
  shefTimeZone: string
): boolean {
  if (!hasSomeDayAvailability(foodItem)) return false;
  const availabilities = getFoodItemAvailabilityRanges(foodItem, shefTimeZone);
  const bookingRange = Interval.fromDateTimes(DateTime.now(), DateTime.now().plus(MAX_BOOKING_TIME_IN_ADVANCE));
  return availabilities.some((availabilityRange) => availabilityRange.overlaps(bookingRange));
}

export function isAvailableOnDate(
  foodItem: FoodItemAvailaibilityFields,
  date: DateTime,
  shefTimeZone: string
): boolean {
  if (!isAvailableOnDay(foodItem, date)) return false;
  const availabilities = getFoodItemAvailabilityRanges(foodItem, shefTimeZone);
  return availabilities.some((availabilityRange) => availabilityRange.contains(date));
}
